import React, { useState } from "react";
import axios from "axios";
import {
  Modal,
  Backdrop,
  Fade,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const WinNumberModal = ({ open, onClose, districtInfo }) => {
  const [resultType, setResultType] = useState("district");
  const [selectedYears, setSelectedYears] = useState([]);

  const electionYears = [
    "2025 PRIMARY",
    "2025 GENERAL",
    "2024 PRIMARY",
    "2024 GENERAL",
    "2023 PRIMARY",
    "2023 GENERAL",
    "2022 PRIMARY",
    "2022 GENERAL",
    "2021 PRIMARY",
    "2021 GENERAL",
    "2020 PRIMARY",
    "2020 GENERAL",
    "2019 PRIMARY",
    "2019 GENERAL",
    "2018 PRIMARY",
    "2018 GENERAL",
    "2017 PRIMARY",
    "2017 GENERAL",
    "2016 PRIMARY",
    "2016 GENERAL",
    "2015 PRIMARY",
    "2015 GENERAL",
    "2014 PRIMARY",
    "2014 GENERAL",
    "2013 PRIMARY",
    "2013 GENERAL",
    "2012 PRIMARY",
    "2012 GENERAL",
    "2011 PRIMARY",
    "2011 GENERAL",
    "2010 PRIMARY",
    "2010 GENERAL",
    "2009 PRIMARY",
    "2009 GENERAL",
    "2008 PRIMARY",
    "2008 GENERAL",
  ];

  const handleResultTypeChange = (event) => {
    setResultType(event.target.value);
  };

  const handleYearChange = (event) => {
    const {
      target: { value },
    } = event;
    const newSelectedYears = typeof value === "string" ? value.split(",") : value;
    setSelectedYears(newSelectedYears);
  };

  const getElectionType = (year) => {
    return year.includes("PRIMARY") ? "PRIMARY" : "GENERAL";
  };

  const isYearDisabled = (year) => {
    if (selectedYears.length === 0) {
      return false;
    }
    const firstSelectedType = getElectionType(selectedYears[0]);
    const currentType = getElectionType(year);
    return firstSelectedType !== currentType;
  };

  const [generating, setGenerating] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleExport = async () => {
    if (!districtInfo || selectedYears.length === 0) return;

    const electionType = selectedYears[0].includes("PRIMARY") ? "PRIMARY" : "GENERAL";

    try {
      setGenerating(true);
      setErrorMsg("");
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/win_number`,
        {
          districtField: districtInfo.field,
          districtValues: districtInfo.values,
          resultType,
          years: selectedYears,
          electionType,
        },
        { responseType: "blob" }
      );

      const contentDisposition = response.headers["content-disposition"] || "";
      let filename = "Win_Number.csv";
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match) filename = match[1];

      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      handleCancel();
    } catch (err) {
      console.error("Win Number export error:", err);
      setErrorMsg("Failed to generate win number. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const handleCancel = () => {
    setSelectedYears([]);
    setResultType("district");
    setErrorMsg("");
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={open}>
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-xl">
            {/* Header */}
            <div className="flex justify-between items-center p-6">
              <h2 className="text-2xl font-semibold">Win Number</h2>
              <button
                onClick={handleCancel}
                className="text-gray-400 hover:text-gray-600"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 pb-6">
              {/* Radio Group */}
              <div className="mb-6">
                <p className="text-gray-700 mb-3">
                  Do you want results by District or Precinct?
                </p>
                <FormControl component="fieldset">
                  <RadioGroup
                    value={resultType}
                    onChange={handleResultTypeChange}
                  >
                    <FormControlLabel
                      value="district"
                      control={<Radio />}
                      label="By District"
                    />
                    <FormControlLabel
                      value="precinct"
                      control={<Radio />}
                      label="By Precinct"
                    />
                  </RadioGroup>
                </FormControl>
              </div>

              {/* Dropdown */}
              <div className="mb-6">
                <FormControl fullWidth>
                  <Select
                    multiple
                    displayEmpty
                    value={selectedYears}
                    onChange={handleYearChange}
                    input={<OutlinedInput />}
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return (
                          <span style={{ color: "#9ca3af" }}>
                            Select one or more election years
                          </span>
                        );
                      }
                      return `${selected.length} election year${
                        selected.length > 1 ? "s" : ""
                      } selected`;
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                        },
                      },
                    }}
                  >
                    {electionYears.map((year) => (
                      <MenuItem 
                        key={year} 
                        value={year}
                        disabled={isYearDisabled(year)}
                      >
                        <Checkbox 
                          checked={selectedYears.indexOf(year) > -1}
                          disabled={isYearDisabled(year)}
                        />
                        <ListItemText primary={year} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              {/* Error */}
              {errorMsg && (
                <p className="text-red-500 text-sm mb-4">{errorMsg}</p>
              )}

              {/* Footer */}
              <div className="flex justify-end gap-3">
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  sx={{
                    textTransform: "none",
                    px: 4,
                    py: 1.5,
                  }}
                >
                  CANCEL
                </Button>
                <Button
                  variant="contained"
                  onClick={handleExport}
                  disabled={selectedYears.length === 0 || generating || !districtInfo}
                  sx={{
                    textTransform: "none",
                    px: 4,
                    py: 1.5,
                  }}
                >
                  {generating ? (
                    <>
                      <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                      Generating...
                    </>
                  ) : (
                    "GENERATE WIN NUMBER"
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Fade>
    </Modal>
  );
};

export default WinNumberModal;
